import cn from 'classnames';
import React, { useEffect, useRef } from 'react';

import { ErrorText } from '../ErrorText/ErrorText';

import styles from './Textarea.module.scss';

export type TextareaProps = {
  value: string;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  error?: string;
  isElastic?: boolean;
  placeholder?: string;
  isResizeDisabled?: boolean;
  outerRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  rows?: number;
  testID?: string;
  textareaClassName?: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
};

export const Textarea: React.FunctionComponent<TextareaProps> = (props) => {
  const {
    rows,
    value,
    outerRef,
    isElastic,
    className,
    textareaClassName,
    testID,
    placeholder,
    isResizeDisabled,
    autoFocus,
    disabled,
    error,
    onBlur,
    onFocus,
    onChange,
    onKeyDown,
  } = props;

  const innerRef = useRef<HTMLTextAreaElement>(null);

  const textareaRef = outerRef || innerRef;

  const autoGrow = (element: HTMLTextAreaElement) => {
    // Measure from a natural height instead of collapsing to a tiny box:
    // collapsing the focused textarea pushes the caret out of its visible box
    // and the browser scrolls the page down to keep the caret in view on
    // every keystroke. With height:auto the box always fits the content, so
    // the caret stays put and no page scrolling happens.
    element.style.height = 'auto';

    const newHeight = element.scrollHeight;

    // A hidden field (closed modal, tabbed-out pane) measures scrollHeight 0.
    // Writing 0px collapses the box and the browser then renders it clamped to
    // its inline min-height — clipping the first line (observed in the
    // document-view rail). Leave height at 'auto' so rows + padding govern
    // until a visible measurement can run (on the next value change).
    if (newHeight > 0) {
      element.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    const element = textareaRef.current;

    if (!element || !isElastic) {
      return;
    }

    if (rows && !element.style.minHeight) {
      const computed = getComputedStyle(element);
      const lineHeight = parseFloat(computed.lineHeight) || 20;
      const paddingTop = parseFloat(computed.paddingTop) || 0;
      const paddingBottom = parseFloat(computed.paddingBottom) || 0;
      const borderTop = parseFloat(computed.borderTopWidth) || 0;
      const borderBottom = parseFloat(computed.borderBottomWidth) || 0;

      element.style.minHeight = `${rows * lineHeight + paddingTop + paddingBottom + borderTop + borderBottom}px`;
    }

    autoGrow(element);
  }, [value, isElastic, rows, textareaRef]);

  useEffect(() => {
    const element = textareaRef.current;

    if (!element || !isElastic) {
      return;
    }

    // A width change (pane resize, pin/unpin, window resize) rewraps the
    // text and changes the line count with no value change: re-measure.
    // The observer also fires for our own height writes; writing the height
    // inside the observation callback itself makes the browser report a
    // ResizeObserver loop on every keystroke. Deferring to the next frame
    // writes outside the observed change and keeps Sentry clean.
    const observer = new ResizeObserver(() =>
      requestAnimationFrame(() => autoGrow(element)),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [isElastic, textareaRef]);

  return (
    <div className={className}>
      <textarea
        autoFocus={autoFocus}
        disabled={disabled}
        ref={textareaRef}
        rows={rows}
        value={value}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        onKeyDown={onKeyDown}
        className={cn(
          styles.container,
          'p2',
          isResizeDisabled && styles.isResizeDisabled,
          isElastic && styles.isElastic,
          error && styles.isError,
          textareaClassName,
        )}
        data-testid={testID}
        placeholder={placeholder}
      />
      <ErrorText error={error} withInput />
    </div>
  );
};
